import { format } from 'date-fns'

import type {
  DashboardDateNavigationGranularity,
  DashboardDateRange,
  DashboardDerivedFilters,
  DashboardFilterDefinition,
  DashboardFilterOptions,
  DashboardRuntimeConfig,
} from './types'
import { SNAPSHOT_DATE_FILTER_KEY } from './snapshot'

type DashboardFilterState = Record<string, unknown>

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/
const LEADING_YMD_RE = /^(\d{4}-\d{2}-\d{2})/

export type DashboardFiltersDefinition = Record<
  string,
  | {
      type: 'in'
      label: string
      options: { options: Array<{ value: string; label: string }> }
    }
  | {
      type: 'number'
      label: string
      options: {
        min: number
        max: number
        modes: ReadonlyArray<'range' | 'single'>
      }
    }
  | {
      type: 'date'
      label: string
      options: { mode: 'range' }
    }
>

export type DashboardNavigationFiltersDefinition = Record<
  string,
  {
    type: 'date-navigator'
    defaultValue: Date
    granularity: DashboardDateNavigationGranularity[] | DashboardDateNavigationGranularity
    defaultGranularity?: DashboardDateNavigationGranularity
  }
>

export type DashboardFilterKeySets = {
  navigationFilterKeys: Set<string>
  numericRangeFilterKeys: Set<string>
  dateFilterKeys: Set<string>
}

export type SerializedDashboardFilters = {
  filterValues: Record<string, unknown[]>
  navigationFilterValues: Record<string, string[]>
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isDateLike(value: unknown): value is Date | string {
  return value instanceof Date || typeof value === 'string'
}

export function toLocalYmd(d: Date | string | undefined): string {
  if (!d) return ''
  if (typeof d === 'string' && YMD_RE.test(d)) return d
  const leadingYmd = typeof d === 'string' ? d.match(LEADING_YMD_RE)?.[1] : undefined
  if (leadingYmd) return leadingYmd
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, 'yyyy-MM-dd')
}

export function parseLocalYmd(d: string | undefined): Date | undefined {
  if (!d || !YMD_RE.test(d)) return undefined
  const parts = d.split('-').map(Number)
  const year = parts[0]
  const month = parts[1]
  const day = parts[2]
  if (year === undefined || month === undefined || day === undefined) return undefined
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined
  }
  return date
}

function normalizeDateRange(value: unknown): DashboardDateRange | undefined {
  if (!value) return undefined
  if (isDateLike(value)) return { from: value, to: value }
  if (!isObjectLike(value)) return undefined

  const from = value.from
  const to = value.to
  return {
    from: isDateLike(from) ? from : undefined,
    to: isDateLike(to) ? to : undefined,
  }
}

function readNavigationDateRange(value: unknown): DashboardDateRange | undefined {
  if (!isObjectLike(value)) return undefined
  return normalizeDateRange(value.value)
}

function readNumberFilterBounds(
  value: unknown
): [number | undefined, number | undefined] | undefined {
  if (!isObjectLike(value)) return undefined
  const mode = value.mode
  if (mode === 'single') {
    const singleValue = typeof value.value === 'number' ? value.value : undefined
    return [singleValue, singleValue]
  }
  if (mode !== 'range' || !isObjectLike(value.from) || !isObjectLike(value.to)) {
    return undefined
  }
  return [
    typeof value.from.value === 'number' ? value.from.value : undefined,
    typeof value.to.value === 'number' ? value.to.value : undefined,
  ]
}

function serializeDateRange(range: DashboardDateRange): [string, string] | undefined {
  const fromIso = toLocalYmd(range.from)
  const toIso = toLocalYmd(range.to)
  return fromIso || toIso ? [fromIso, toIso] : undefined
}

export function buildFilterDefinitions(
  filterSpecs: Record<string, DashboardFilterDefinition> | undefined,
  filterOptions: DashboardFilterOptions | undefined
): DashboardFiltersDefinition | undefined {
  if (!filterSpecs || Object.keys(filterSpecs).length === 0) return undefined

  const result: DashboardFiltersDefinition = {}

  for (const [key, spec] of Object.entries(filterSpecs)) {
    if (spec.type === 'date') {
      result[key] = {
        type: 'date',
        label: spec.label,
        options: { mode: 'range' },
      }
      continue
    }

    const options = filterOptions?.[key]
    if (options === undefined) continue

    if (spec.type === 'numericRange') {
      if (Array.isArray(options)) continue
      if (options.min === options.max) continue
      result[key] = {
        type: 'number',
        label: spec.label,
        options: {
          min: options.min,
          max: options.max,
          modes: ['range'],
        },
      }
      continue
    }

    if (spec.type === 'in') {
      if (!Array.isArray(options) || options.length === 0) continue
      result[key] = {
        type: 'in',
        label: spec.label,
        options: {
          options: options.map((v) => ({ value: v, label: v })),
        },
      }
    }
  }

  return Object.keys(result).length > 0 ? result : undefined
}

export function buildNavigationFilterDefinitions(
  navigationFilters: DashboardDerivedFilters['navigationFilters'],
  config: Pick<DashboardRuntimeConfig, 'snapshot' | 'snapshotDate'>
): DashboardNavigationFiltersDefinition | undefined {
  const today = new Date()
  const result: DashboardNavigationFiltersDefinition = {}

  if (navigationFilters) {
    for (const [key, spec] of Object.entries(navigationFilters)) {
      if (spec.type !== 'dateNavigation') continue
      result[key] = {
        type: 'date-navigator',
        defaultValue: today,
        granularity: spec.granularities,
        ...(spec.defaultGranularity ? { defaultGranularity: spec.defaultGranularity } : {}),
      }
    }
  }

  if (config.snapshot === true) {
    result[SNAPSHOT_DATE_FILTER_KEY] = {
      type: 'date-navigator',
      defaultValue: parseLocalYmd(config.snapshotDate) ?? today,
      granularity: 'day',
    }
  }

  return Object.keys(result).length > 0 ? result : undefined
}

export function buildDashboardFilterKeySets(
  derivedFilters: DashboardDerivedFilters,
  config: Pick<DashboardRuntimeConfig, 'snapshot'>
): DashboardFilterKeySets {
  const navigationFilterKeys = new Set(Object.keys(derivedFilters.navigationFilters ?? {}))
  if (config.snapshot === true) navigationFilterKeys.add(SNAPSHOT_DATE_FILTER_KEY)

  const numericRangeFilterKeys = new Set<string>()
  const dateFilterKeys = new Set<string>()

  if (derivedFilters.filters) {
    for (const [key, spec] of Object.entries(derivedFilters.filters)) {
      if (spec.type === 'numericRange') numericRangeFilterKeys.add(key)
      if (spec.type === 'date') dateFilterKeys.add(key)
    }
  }

  return {
    navigationFilterKeys,
    numericRangeFilterKeys,
    dateFilterKeys,
  }
}

export function serializeDashboardFilters(input: {
  filters: DashboardFilterState
  navigationFilterKeys: ReadonlySet<string>
  numericRangeFilterKeys: ReadonlySet<string>
  dateFilterKeys: ReadonlySet<string>
  snapshotDate?: string
  onSnapshotDateChange?: (isoDate: string | null) => void
}): SerializedDashboardFilters {
  const filterValues: Record<string, unknown[]> = {}
  const navigationFilterValues: Record<string, string[]> = {}

  for (const [key, value] of Object.entries(input.filters)) {
    if (input.navigationFilterKeys.has(key)) {
      const range = readNavigationDateRange(value)
      if (!range) continue
      const serializedRange = serializeDateRange(range)
      if (serializedRange) navigationFilterValues[key] = serializedRange
      const fromIso = serializedRange?.[0]
      if (key === SNAPSHOT_DATE_FILTER_KEY && fromIso) {
        const initial = input.snapshotDate ? toLocalYmd(input.snapshotDate) : undefined
        input.onSnapshotDateChange?.(initial !== fromIso ? fromIso : null)
      }
      continue
    }

    if (input.numericRangeFilterKeys.has(key)) {
      const bounds = readNumberFilterBounds(value)
      if (!bounds) continue
      const [from, to] = bounds
      const fromStr = typeof from === 'number' && Number.isFinite(from) ? String(from) : ''
      const toStr = typeof to === 'number' && Number.isFinite(to) ? String(to) : ''
      if (fromStr || toStr) {
        filterValues[key] = [fromStr, toStr]
      }
      continue
    }

    if (input.dateFilterKeys.has(key)) {
      const range = normalizeDateRange(value)
      if (!range) continue
      const serializedRange = serializeDateRange(range)
      if (serializedRange) filterValues[key] = serializedRange
      continue
    }

    if (Array.isArray(value) && value.length > 0) {
      filterValues[key] = value
    }
  }

  return { filterValues, navigationFilterValues }
}
